// Where a project lives on the web.
//
// The mapping is convention, enforced at the edge: the CloudFront Function reads
// the Host header and derives the slug from it (`{slug}.s3p.dev` → `slug`,
// `s3p.dev` → `platform`). Nothing in the UI knew that convention, so the
// projects list showed slugs as plain text and the user had to *retype* the URL
// of their own app. These helpers are the single place that knows it.
//
// Derived from the current origin rather than hardcoded, so a platform deployed
// at a different root domain still links correctly. When a project attaches a
// CUSTOM domain (app.domains.add), that host is recorded on the project and
// should be preferred for the app link — the `/server` console stays on the
// canonical host, because that's where its Cognito callback is registered.

const FALLBACK_ROOT = "s3p.dev";

/** The platform's root domain — `s3p.dev` whether we're served from `s3p.dev`
 *  or `platform.s3p.dev`. Falls back on localhost/dev. */
export function rootDomain(): string {
  const host = window.location.hostname;
  if (!host || host === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(host)) {
    return FALLBACK_ROOT;
  }
  const labels = host.split(".");
  // `platform.s3p.dev` → strip the project label; `s3p.dev` → as-is.
  return labels.length > 2 ? labels.slice(1).join(".") : host;
}

/** The canonical host for a project: `{slug}.s3p.dev`. */
export function projectHost(slug: string): string {
  return `${slug}.${rootDomain()}`;
}

/** The project's app (what its end users visit). */
export function projectUrl(slug: string): string {
  return `https://${projectHost(slug)}/`;
}

/** The CONSOLE host — the one place apps are managed.
 *
 *  Not a per-project page and not a copy in each bucket: one deployment, one
 *  origin. `{slug}.s3p.dev/server` redirects here (at the edge), and every link
 *  in the product points here. */
export const CONSOLE_ORIGIN = `https://console.${rootDomain()}`;

/** The community. One constant so the landing and the footer can't drift apart —
 *  a dead invite link in one of two places is the classic way this rots. */
export const DISCORD_INVITE = "https://discord.gg/FZF7Bs4Nk";

/** The MARKETING host — the apex (s3p.dev). Where the wordmark points from
 *  inside the console: the same bundle serves both, but they're different
 *  origins, so this must be an absolute URL and never a <Link>. */
export const MARKETING_ORIGIN = `https://${rootDomain()}`;

/** Where "create an account / new app" lives: `/signup` on BOTH hosts.
 *
 *  Historically this differed per host (`/new` on the console, `/app/new` on
 *  marketing). It's now the same clean path everywhere — `/signup` — with the
 *  old paths kept as redirects (see App.tsx) so links already in the world
 *  (emails, bookmarks) still land. Kept as a function so callers never hardcode
 *  it and a future host split stays a one-line change. */
export function newAppPath(): string {
  return "/signup";
}

/** Where a "sign up / create an app" button should actually POINT.
 *
 *  Sign-up establishes a session, and sessions are per-origin — so the funnel
 *  has to run on the CONSOLE host or the user signs in once on the apex and
 *  again the moment they reach their apps. The apex redirects `/signup` there
 *  anyway (see ToConsole in App.tsx); this skips the extra hop for the links we
 *  control, which includes the landing page's primary CTA.
 *
 *  Absolute off the console host, relative on it — so it stays a plain in-app
 *  navigation where that's what it is. */
export function signupHref(): string {
  return onConsoleHost() ? newAppPath() : `${CONSOLE_ORIGIN}${newAppPath()}`;
}

/** True when we're being served AS the console (console.s3p.dev), rather than as
 *  the marketing site (s3p.dev). The host decides what `/` means. */
export function onConsoleHost(): boolean {
  return window.location.hostname.startsWith("console.");
}

/** THE ONE SEGMENT THAT KEEPS PROJECT SLUGS OUT OF THE CONSOLE'S OWN NAMESPACE.
 *
 *  Project consoles used to live at `console.s3p.dev/{slug}`, i.e. the SAME
 *  namespace as the console's own pages — so every route this app adds silently
 *  steals a slug, and every slug a customer picks can shadow a page. That is not
 *  theoretical; it shipped twice. `console.s3p.dev/console` rendered the app LIST
 *  instead of the `console` project (the static route out-ranks `/:slug`), and
 *  `/app` fell through to `/:slug/*` and opened the console of a project called
 *  "app" — a plausible-looking page about nothing. Each was patched with one more
 *  static redirect, which is a denylist that has to grow forever and can only ever
 *  be discovered by a customer hitting it.
 *
 *  Slugs are `[a-z0-9][a-z0-9-]*` with NO reserved words anywhere in provisioning
 *  (verified: nothing in the SDK, the CLIs or the provisioner rejects `admin`,
 *  `signin`, `docs`, `pricing`, `upgrade`, `security` or `signup`). So the fix has
 *  to be structural: give projects their own path segment, and the two namespaces
 *  can never intersect again — whatever pages this console grows, and whatever
 *  slug anyone registers.
 *
 *  Change this constant and every producer moves with it (that is the point of it
 *  being one constant); the legacy `/{slug}` redirect in App.tsx keeps old links,
 *  emails, docs and bookmarks working. */
export const PROJECT_PATH_PREFIX = "/p";

/** In-app path to a project's console, optionally deep into one of its panels.
 *  `rest` is the path BELOW the project (e.g. "database"), no leading slash
 *  required. */
export function projectPath(slug: string, rest = ""): string {
  const tail = rest.replace(/^\/+/, "");
  return `${PROJECT_PATH_PREFIX}/${slug}${tail ? `/${tail}` : ""}`;
}

/** A project's console, absolute. */
export function consoleUrl(slug: string): string {
  return `${CONSOLE_ORIGIN}${projectPath(slug)}`;
}

/** The two commands that wire an existing directory to a project and ship it. */
export function deployCommand(slug: string): string {
  return `s3p init ${slug} && s3p deploy`;
}
